import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, MessageCircle, Shield, CheckCircle2 } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import NetworkGuide from "./NetworkGuide";
import { Step, Tip, MediaThumbs } from "./publish/shared";
import { FunnelConnect } from "./publish/FunnelConnect";
import { MetaOptionCard } from "./publish/MetaOptionCard";

const WaGate = () => (
  <div data-testid="publish-wa-gate" className="rounded-2xl border border-green-500/40 bg-green-500/5 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
    <div className="flex items-start gap-3">
      <MessageCircle className="text-green-400 mt-0.5 shrink-0" size={22} />
      <div>
        <div className="font-semibold text-green-400">Configura tu WhatsApp antes de publicar</div>
        <p className="text-sm text-zinc-400">Tus prospectos calificados llegan a tu WhatsApp individual. Sin él, perderías esos contactos.</p>
      </div>
    </div>
    <a href="/dashboard/perfil" data-testid="goto-profile-wa" className="bg-green-500 hover:bg-green-400 text-black font-bold rounded-full px-5 py-2.5 text-sm whitespace-nowrap text-center">
      Configurar ahora
    </a>
  </div>
);

const WhatsAppSteps = ({ text, captionWithLink, link, copy, images, videos }) => (
  <>
    <Step n={1} title="Información que debe promocionar tu campaña" desc="Este es el mensaje que enviarás. Cópialo y pégalo en tu WhatsApp.">
      <div className="rounded-xl bg-black border border-white/10 p-4">
        <p className="text-sm text-zinc-200 whitespace-pre-wrap">{text || "Genera el texto en el paso anterior."}</p>
      </div>
      <Button data-testid="copy-text" onClick={() => copy(captionWithLink)} className="mt-3 bg-purple-600 hover:bg-purple-500 rounded-xl">
        <Copy size={16} className="mr-2" /> Copiar texto {link ? "y enlace" : ""}
      </Button>
    </Step>

    <Step n={2} title="Selecciona y descarga imágenes o videos" desc="Descarga los archivos a tu teléfono para adjuntarlos en WhatsApp.">
      <MediaThumbs images={images} videos={videos} />
    </Step>

    <Step n={3} title="Descarga esos archivos a tu WhatsApp y publícalo" desc="Abre WhatsApp, crea una Lista de Difusión (no grupos), adjunta el archivo y pega el texto.">
      <div className="flex flex-wrap gap-3">
        <a data-testid="open-whatsapp" href="https://web.whatsapp.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl px-5 py-3 text-sm">
          <MessageCircle size={18} /> Abrir WhatsApp
        </a>
      </div>
      <p className="text-xs text-zinc-500 mt-3 flex items-start gap-2">
        <Tip text="Usa Listas de Difusión: el mensaje llega como privado a cada contacto que te tenga agregado, evitando el spam de grupos." />
        Tip: usa <strong className="text-zinc-300 mx-1">Listas de Difusión</strong> para llegar a muchos sin caer en spam. Personaliza el saludo cuando puedas.
      </p>
    </Step>
  </>
);

export default function PublishPanel({ campaign, funnelLink, onMethodChange }) {
  const { user } = useAuth();
  const waMissing = user && !user.whatsapp;
  const text = campaign?.text?.text || "";
  const images = campaign?.images || [];
  const videos = (campaign?.videos || []).filter((v) => v.status === "done" && v.video_url);
  const method = campaign?.publish_method;

  const buildLink = (slug) => `${window.location.origin}/f/${slug}`;
  const buildLanding = (slug) => `${window.location.origin}/p/${slug}`;
  const connectedLink = campaign?.landing_slug ? buildLanding(campaign.landing_slug) : (campaign?.funnel_slug ? buildLink(campaign.funnel_slug) : (funnelLink || ""));
  const [link, setLink] = useState(connectedLink);
  const [funnels, setFunnels] = useState([]);
  const [selFunnel, setSelFunnel] = useState("");
  const [connecting, setConnecting] = useState(null); // "ai" | "existing"
  const [metaStatus, setMetaStatus] = useState(null);
  const [publishingMeta, setPublishingMeta] = useState(false);

  useEffect(() => {
    api.get("/funnels").then((r) => {
      setFunnels(r.data || []);
      if (r.data?.length) setSelFunnel(r.data[0].funnel_id);
    }).catch(() => {});
    if (campaign?.type === "social") {
      api.get("/meta/status").then((r) => setMetaStatus(r.data)).catch(() => setMetaStatus({ connected: false }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const publishMeta = async (targets) => {
    if (!campaign?.funnel_slug && !link) {
      return toast.error("Conecta primero tu embudo (arriba) para incluir el enlace de captura.");
    }
    setPublishingMeta(true);
    try {
      const { data } = await api.post("/meta/publish", { gc_id: campaign.gc_id, targets });
      const oks = Object.entries(data.results || {}).filter(([, v]) => v.ok).map(([k]) => k);
      const fails = Object.entries(data.results || {}).filter(([, v]) => !v.ok);
      if (oks.length) toast.success(`Publicado en: ${oks.join(", ")}`);
      fails.forEach(([k, v]) => toast.error(`${k}: ${v.error}`));
      onMethodChange?.("auto");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setPublishingMeta(false);
    }
  };

  const connectFunnel = async (mode) => {
    if (mode === "existing" && !selFunnel) return toast.error("Elige un embudo de la lista");
    setConnecting(mode);
    try {
      const body = mode === "ai" ? { mode: "ai" } : { mode: "existing", funnel_id: selFunnel };
      const { data } = await api.put(`/guided/campaigns/${campaign.gc_id}/funnel`, body);
      setLink(buildLink(data.slug));
      toast.success(mode === "ai" ? "¡Embudo creado por la IA y conectado!" : "Embudo conectado a tu campaña");
      if (mode === "ai") api.get("/funnels").then((r) => setFunnels(r.data || [])).catch(() => {});
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setConnecting(null);
    }
  };

  const captionWithLink = link ? `${text}\n\n👉 ${link}` : text;

  const copy = (t, msg = "¡Copiado!") => {
    navigator.clipboard.writeText(t);
    toast.success(msg);
  };

  const setMethod = async (m) => {
    if (waMissing) return toast.error("Configura tu WhatsApp en Perfil del Líder antes de publicar.");
    try {
      await api.put(`/guided/campaigns/${campaign.gc_id}/publish-method`, { method: m });
      onMethodChange?.(m);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const funnelConnectBlock = (
    <FunnelConnect
      campaign={campaign} connectedLink={connectedLink} funnels={funnels}
      selFunnel={selFunnel} setSelFunnel={setSelFunnel} connecting={connecting} connectFunnel={connectFunnel}
    />
  );

  if (campaign.type === "whatsapp") {
    return (
      <div data-testid="publish-whatsapp" className="space-y-5">
        {waMissing && <WaGate />}
        {funnelConnectBlock}
        <WhatsAppSteps text={text} captionWithLink={captionWithLink} link={link} copy={copy} images={images} videos={videos} />
      </div>
    );
  }

  return (
    <div data-testid="publish-social" className="space-y-5">
      {waMissing && <WaGate />}
      {funnelConnectBlock}

      <p className="text-sm text-zinc-400">Elige cómo quieres publicar tu campaña en redes sociales:</p>
      <div className="grid md:grid-cols-2 gap-4">
        <MetaOptionCard metaStatus={metaStatus} publishingMeta={publishingMeta} publishMeta={publishMeta} />

        <button
          data-testid="option-b"
          onClick={() => setMethod("manual")}
          className={`text-left rounded-2xl border p-5 transition-all ${method === "manual" ? "border-purple-500 bg-purple-600/10" : "border-white/10 bg-[#121212] hover:border-purple-500/50"}`}
        >
          <div className="flex items-center justify-between">
            <Shield className="text-purple-400" size={24} />
            {method === "manual" && <CheckCircle2 className="text-purple-400" size={20} />}
          </div>
          <h3 className="font-display font-bold text-lg mt-3">🛡️ Opción B: Publicación Segura (Manual)</h3>
          <p className="text-sm text-zinc-400 mt-1">Evita el spam y maximiza tu alcance orgánico.</p>
        </button>
      </div>

      {method === "manual" && (
        <div data-testid="manual-checklist" className="rounded-2xl border border-purple-500/30 bg-[#0e0a16] p-5 space-y-5">
          <Step n={1} title="📥 Descarga tu video o imagen" desc="Guarda el archivo en alta calidad en tu dispositivo.">
            <MediaThumbs images={images} videos={videos} />
          </Step>
          <NetworkGuide campaign={campaign} link={link} Step={Step} />
        </div>
      )}
    </div>
  );
}
